import axios from "axios";
import { put, putResolve, takeLatest } from "redux-saga/effects";

import {
  EParticipant,
  IJoinInterviewArgs,
  ILeaveParticipantAction,
  IStartInterviewArgs,
  IUserAllowParticipantAction,
  IUserStatusArgs,
  TJoinInterviewPayload,
} from "@/types/auth.types";
import { IInitialState } from "@/types/initialState.types";
import {
  setApiCallError,
  setApiCallInProgress,
  setApiCallSuccess,
} from "@/actions/apiCallStatus.action";
import { showToast } from "@/actions/appStatus.action";
import { updateAPIProgress } from "@/actions/progress.action";
import { EToastType } from "@/components/common/AlertToast/AlertToast.types";
import {
  INTERVIEW_ALLOW_USER,
  INTERVIEW_END_FOR_ME,
  INTERVIEW_LEAVE_FOR_ALL,
} from "@/constants/SocketEventConstants";
import { removeLocalStorageItem } from "@/utils/localStorageHelper";
import { LocalStorageKey } from "@/types/candidateLogin.types";
import apiErrorHandler from "@/utils/apiErrorHandler";
import {
  ALLOW_USER_API_ERROR_MESSAGE,
  GET_USER_STATUS_API_HTTP_STATUS_CODES,
  GET_USER_STATUS_ERROR_MESSAGE,
  JOIN_INTERVIEW_API_HTTP_STATUS_CODES,
  JOIN_INTERVIEW_ERROR_MESSAGE,
  LEAVE_ALL_API_ERROR_MESSAGE,
  LEAVE_API_ERROR_MESSAGE,
  USER_ACTIONS_API_HTTP_STATUS_CODES,
} from "@/constants/AuthConstants";
import { EInterviewType } from "@/types/interview.types";
import {
  allowUserAPI,
  getUserStatusAPI,
  joinInterviewAPI,
  leaveAllUserAPI,
  leaveUserAPI,
  startInterviewAPI,
} from "../api/auth.api";
import {
  ALLOW_USER,
  END_INTERVIEW_FOR_ALL,
  GET_ALL_PARTICIPANTS,
  GET_USER_STATUS,
  HIDE_MODAL,
  JOIN_INTERVIEW,
  LEAVE_INTERVIEW,
  SET_STARTED_TIME,
  START_INTERVIEW,
  SET_OTP_ERROR,
  SET_OTP_LOADING,
  GRACEFUL_SHUTDOWN,
} from "../constants/ActionTypes";
import { USER_STATUS_API } from "../constants/ApiNameConstants";
import { DO_CENTRAL_SERVICE_URL } from "../constants/AppConstants";
import { HTTP_STATUS_CODES } from "../constants/HttpConstants";
import { INTERVIEW_STATUS } from "../constants/InterviewStatus";
import { DYNAMIC_ROUTES_CONSTANT, ROUTES } from "../constants/RouteConstants";
import { encodeData } from "../utils/encodeDecode.utils";
import {
  goToAddQuestionRoute,
  goToDoSelectFeedbackRoute,
  goToInterviewFeedbackRoute,
  goToLobbyRoute,
  goToQuestionRoute,
} from "../utils/switchRoute.utils";
import {
  handleRemoveAudioVideo,
  leaveUserFromCall,
  stopScreenShare,
} from "../utils/zoom.utils";
import { call, errorHandler, select } from "./utils.saga";

const { REDIRECTION_ROUTE } = ROUTES;
const { ACCESS_CODE } = DYNAMIC_ROUTES_CONSTANT;

function* joinInterview({ payload }: IJoinInterviewArgs) {
  try {
    yield put({ type: `${SET_OTP_LOADING}_SUCCESS` });
    const { participantType, name, email, interviewSlug, navigate, otp } =
      payload;
    let body: TJoinInterviewPayload = {
      participant_type: participantType,
    };
    if (participantType === EParticipant.CANDIDATE) {
      body = {
        ...body,
        participant_name: name,
        participant_email: email,
        otp,
      };
    }
    yield call(joinInterviewAPI, interviewSlug, body);
    removeLocalStorageItem(LocalStorageKey.CandidateInfo);
    removeLocalStorageItem(LocalStorageKey.SendOTP);
    if (navigate) {
      goToLobbyRoute(navigate, interviewSlug, { replace: true });
    }
  } catch (error: unknown) {
    if (
      payload.participantType === EParticipant.INTERVIEWER &&
      axios.isAxiosError(error) &&
      error.response?.status === HTTP_STATUS_CODES.UNAUTHORIZED
    ) {
      const accessCode = yield* select(
        (state: IInitialState) => state.interview?.accessCode,
      );
      if (accessCode) {
        const redirecrtionUrl = `${DO_CENTRAL_SERVICE_URL}/login?next=${
          window.location.origin
        }${REDIRECTION_ROUTE.replace(ACCESS_CODE, accessCode)}`;
        window.location.href = redirecrtionUrl;
      }
    } else {
      const message = yield* call(
        apiErrorHandler,
        error,
        JOIN_INTERVIEW_API_HTTP_STATUS_CODES,
        JOIN_INTERVIEW_ERROR_MESSAGE,
      );
      const { interviewType, isCandidate } = yield* select(
        (state: IInitialState) => ({
          interviewType: state.interview?.interview_type,
          isCandidate: state.auth.userIdentityData?.isCandidate,
        }),
      );
      if (message) {
        if (interviewType === EInterviewType.AD_HOC && isCandidate) {
          yield put({ type: `${SET_OTP_ERROR}_SUCCESS`, payload: { message } });
        } else {
          yield put(
            showToast({
              message,
              type: EToastType.ERROR,
            }),
          );
        }
      }
    }
  } finally {
    yield put({ type: `${SET_OTP_LOADING}_COMPLETED` });
  }
}

function* startInterview(args: IStartInterviewArgs) {
  try {
    const { firstQuestionSlug, socket } = yield* select(
      (state: IInitialState) => ({
        firstQuestionSlug: Object.keys(state.questionsDetails.questions)[0],
        socket: state.socket.socket,
      }),
    );
    const { interviewSlug, navigate, fromInterviewFeedback } = args;
    const startInterviewResponse = yield* call(
      startInterviewAPI,
      interviewSlug,
    );
    if (startInterviewResponse.status === HTTP_STATUS_CODES.CREATED) {
      yield putResolve({
        type: `${START_INTERVIEW}_SUCCESS`,
        payload: startInterviewResponse.data,
      });

      if (socket?.disconnected) {
        socket.connect();
      }
      if (fromInterviewFeedback) {
        navigate(-1);
      } else if (firstQuestionSlug) {
        goToQuestionRoute(navigate, interviewSlug, firstQuestionSlug, {
          replace: true,
        });
      } else if (interviewSlug) {
        goToAddQuestionRoute(navigate, interviewSlug, { replace: true });
      }
    }
  } catch (error: unknown) {
    const message = yield* call(errorHandler, error);
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* getUserStatus(args: IUserStatusArgs) {
  const { interviewSlug, skipRedirect } = args;
  try {
    yield put(setApiCallInProgress(USER_STATUS_API));
    const resp = yield* call(getUserStatusAPI, interviewSlug);
    yield putResolve({
      type: `${GET_USER_STATUS}_SUCCESS`,
      payload: resp.data,
    });
    yield put(setApiCallSuccess(USER_STATUS_API));
  } catch (error: unknown) {
    yield put(setApiCallError(USER_STATUS_API));
    const message = yield* call(
      apiErrorHandler,
      error,
      GET_USER_STATUS_API_HTTP_STATUS_CODES,
      GET_USER_STATUS_ERROR_MESSAGE,
      skipRedirect,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* allowUser({ payload }: IUserAllowParticipantAction) {
  try {
    const { interviewSlug, candidateSlug, candidateEmail } = payload;
    const allowUserResponse = yield* call(allowUserAPI, interviewSlug, {
      candidate_slug: candidateSlug,
    });

    yield put({
      type: GET_ALL_PARTICIPANTS,
      payload: {
        interviewSlug,
      },
    });
    yield put({
      type: SET_STARTED_TIME,
      payload: {
        started_at: allowUserResponse.data.started_at,
      },
    });
    const socket = yield* select((state: IInitialState) => state.socket.socket);
    socket?.emit(
      INTERVIEW_ALLOW_USER,
      encodeData({
        candidateEmail,
        startTime: allowUserResponse.data.started_at,
      }),
    );
  } catch (error) {
    const message = yield* call(
      apiErrorHandler,
      error,
      USER_ACTIONS_API_HTTP_STATUS_CODES,
      ALLOW_USER_API_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* leaveUser(args: ILeaveParticipantAction) {
  try {
    const { interviewSlug, navigate } = args.payload;
    yield call(leaveUserAPI, interviewSlug);
    const { name, client, socket, stream, scoreCardEnabled, redirectionUrl } =
      yield* select((state: IInitialState) => ({
        name: state.auth.userIdentityData?.name,
        client: state.zoomClient.client,
        socket: state.socket.socket,
        stream: state.zoomClient.stream,
        scoreCardEnabled: state.interview?.settings?.score_card_enabled,
        redirectionUrl: state.interview?.interviewer?.redirection_url,
      }));

    socket?.emit(
      INTERVIEW_END_FOR_ME,
      encodeData({
        name,
      }),
    );
    yield call(handleRemoveAudioVideo, stream, client);
    yield call(leaveUserFromCall, client);
    socket?.disconnect();
    if (!scoreCardEnabled && redirectionUrl) {
      window.location.replace(redirectionUrl);
    } else {
      yield putResolve({
        type: `${LEAVE_INTERVIEW}_SUCCESS`,
        payload: {
          is_leaved: true,
        },
      });
      if (scoreCardEnabled) {
        goToInterviewFeedbackRoute(navigate, interviewSlug);
      } else {
        goToDoSelectFeedbackRoute(navigate, interviewSlug);
      }
    }
  } catch (error: unknown) {
    const message = yield* call(
      apiErrorHandler,
      error,
      USER_ACTIONS_API_HTTP_STATUS_CODES,
      LEAVE_API_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

function* leaveAllUser({ payload }: ILeaveParticipantAction) {
  try {
    yield put(updateAPIProgress(true));

    const { interviewSlug, navigate, fromEvent, isAutoSubmit } = payload;

    const {
      socket,
      client,
      stream,
      isInterviewer,
      isCandidate,
      redirectionUrl,
      scoreCardEnabled,
    } = yield* select((state: IInitialState) => ({
      socket: state.socket.socket,
      client: state.zoomClient.client,
      stream: state.zoomClient.stream,
      isInterviewer: state.auth.userIdentityData?.isInterviewer,
      isCandidate: state.auth.userIdentityData?.isCandidate,
      scoreCardEnabled: state.interview?.settings?.score_card_enabled,
      redirectionUrl: state.interview?.interviewer?.redirection_url,
    }));

    if (!fromEvent && isInterviewer) {
      yield call(leaveAllUserAPI, interviewSlug);
      if (!isAutoSubmit) {
        socket?.emit(INTERVIEW_LEAVE_FOR_ALL, encodeData({ interviewSlug }));
      }
    }
    yield call(handleRemoveAudioVideo, stream, client);
    if (isCandidate) yield call(stopScreenShare, client, stream);
    yield call(leaveUserFromCall, client, isInterviewer ?? false);

    const interval = setTimeout(() => {
      socket?.disconnect();
      clearTimeout(interval);
    }, 10 * 1000);

    if (isInterviewer && !scoreCardEnabled && redirectionUrl) {
      window.location.replace(redirectionUrl);
    } else {
      yield putResolve({
        type: `${END_INTERVIEW_FOR_ALL}_SUCCESS`,
        payload: {
          interview_status: INTERVIEW_STATUS.FEEDBACK_PENDING,
          is_leaved: true,
        },
      });
      yield put({
        type: HIDE_MODAL,
      });
      if (isInterviewer && scoreCardEnabled) {
        goToInterviewFeedbackRoute(navigate, interviewSlug, { replace: true });
      } else {
        goToDoSelectFeedbackRoute(navigate, interviewSlug, { replace: true });
      }
    }
  } catch (error: unknown) {
    const message = yield* call(
      apiErrorHandler,
      error,
      USER_ACTIONS_API_HTTP_STATUS_CODES,
      LEAVE_ALL_API_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  } finally {
    yield put(updateAPIProgress(false));
  }
}

function* gracefulShutdown() {
  const { socket, client, stream, isCandidate, isInterviewer } = yield* select(
    (state: IInitialState) => ({
      socket: state.socket.socket,
      client: state.zoomClient.client,
      stream: state.zoomClient.stream,
      isCandidate: state.auth.userIdentityData?.isCandidate,
      isInterviewer: state.auth.userIdentityData?.isInterviewer,
    }),
  );

  if (socket?.connected) {
    socket.disconnect();
  }

  if (client && stream) {
    yield call(handleRemoveAudioVideo, stream, client);
    if (isCandidate) yield call(stopScreenShare, client, stream);
    yield call(leaveUserFromCall, client, isInterviewer ?? false);
  }
}

export default function* loadAuthSaga() {
  yield takeLatest(JOIN_INTERVIEW, joinInterview);
  yield takeLatest(START_INTERVIEW, startInterview);
  yield takeLatest(LEAVE_INTERVIEW, leaveUser);
  yield takeLatest(GET_USER_STATUS, getUserStatus);
  yield takeLatest(ALLOW_USER, allowUser);
  yield takeLatest(END_INTERVIEW_FOR_ALL, leaveAllUser);
  yield takeLatest(GRACEFUL_SHUTDOWN, gracefulShutdown);
}
